import {CSSObject} from "@emotion/react";
import {getTheme, Theme, Font, StyleFn, forBrands} from "./internal";

type BaseProps = {
  theme: Theme;
  crossBrand?: boolean;
};

type WeightNames = {
  thin: number;
  ultraLight: number;
  extraLight: number;
  light: number;
  regular: number;
  book: number;
  medium: number;
  demiBold: number;
  semiBold: number;
  bold: number;
  black: number;
  heavy: number;
};

export const getFontWeight = (
  weight: keyof WeightNames
): Pick<CSSObject, "fontWeight"> => {
  const weightNames: WeightNames = {
    thin: 100,
    ultraLight: 100,
    extraLight: 200,
    light: 300,
    book: 400,
    regular: 400,
    medium: 500,
    demiBold: 600,
    semiBold: 600,
    bold: 700,
    black: 800,
    heavy: 900,
  };

  return {fontWeight: weightNames[weight]};
};

export const getFont =
  (fontKey: keyof Theme["font"]): StyleFn<BaseProps, Font | undefined> =>
  ({theme, crossBrand}): CSSObject | undefined => {
    const {brandFont, brandFontAlt} = getTheme({theme, crossBrand});
    switch (fontKey) {
      case "primary": {
        const fontWeight = forBrands(
          theme,
          {
            br: getFontWeight("book"),
            crossBrand: getFontWeight("regular"),
            default: getFontWeight("regular"),
          },
          crossBrand
        );
        return {...brandFont, ...(fontWeight as CSSObject)};
      }
      case "secondary": {
        const fontWeight = forBrands(
          theme,
          {
            at: getFontWeight("demiBold"),
            br: getFontWeight("semiBold"),
            crossBrand: getFontWeight("semiBold"),
            default: getFontWeight("bold"),
          },
          crossBrand
        );
        return {...brandFont, ...(fontWeight as CSSObject)};
      }
      case "tertiary": {
        const fontFamily = forBrands(theme, {
          br: brandFontAlt,
          brfs: brandFontAlt,
          at: brandFontAlt,
          default: brandFont,
        });
        const fontWeight = forBrands(
          theme,
          {
            at: getFontWeight("medium"),
            on: getFontWeight("heavy"),
            crossBrand: getFontWeight("light"),
            default: getFontWeight("regular"),
          },
          crossBrand
        );
        return {
          ...(fontFamily as CSSObject),
          ...(fontWeight as CSSObject),
        };
      }
      default:
        return getTheme({theme, crossBrand}).brandFont;
    }
  };
